import React, { useState } from 'react';
import { Link2, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import './ShareButtons.css';

const ShareButtons = ({ artwork, url }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const description = artwork
    ? `${artwork.title} — ${artwork.medium || ''} · C. LLAGUNO`
    : 'C. LLAGUNO — Contemporary Artist';

  // Prefer a real https image URL (via backend proxy that decodes the base64
  // data URI stored in Mongo) so Pinterest / OG previews actually work.
  const rawImage = artwork?.image || '';
  const isHttps = /^https?:\/\//.test(rawImage);
  const publicImage = artwork?.slug
    ? `${BACKEND_URL}/api/artworks/image/${artwork.slug}`
    : (isHttps ? rawImage : '');

  const pinterestHref = publicImage
    ? `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(publicImage)}&description=${encodeURIComponent(description)}`
    : `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(description)}`;

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `${description}\n${shareUrl}`
  )}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div className="share-buttons" data-testid="share-buttons">
      <span className="share-label">{t('share_title')}:</span>
      <button
        type="button"
        onClick={handleCopy}
        className={`share-btn share-btn-copy ${copied ? 'copied' : ''}`}
        data-testid="share-copy-btn"
        aria-label={t('share_copy')}
      >
        {copied ? <Check size={14} /> : <Link2 size={14} />}
        <span>{copied ? t('share_copied') : t('share_copy')}</span>
      </button>
      <a
        href={pinterestHref}
        target="_blank"
        rel="noopener noreferrer"
        className="share-btn share-btn-pin"
        data-testid="share-pinterest-btn"
        aria-label={t('share_pinterest')}
        data-pin-do="none"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.219-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
        </svg>
        <span>{t('share_pinterest')}</span>
      </a>
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        className="share-btn share-btn-wa"
        data-testid="share-whatsapp-btn"
        aria-label={t('share_whatsapp')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.711.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004c-1.97 0-3.9-.53-5.586-1.53l-.4-.238-4.155 1.09 1.11-4.052-.26-.415a9.876 9.876 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.827 9.827 0 012.893 6.992c-.003 5.45-4.437 9.884-9.884 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.354.101 11.933c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.447h.006c6.585 0 11.946-5.355 11.949-11.938a11.9 11.9 0 00-3.48-8.408z"/>
        </svg>
        <span>{t('share_whatsapp')}</span>
      </a>
    </div>
  );
};

export default ShareButtons;
